/**
 * SSE / WebSocket cleanup registry — a module-level singleton that
 * tracks every live stream opened by hooks across the app.
 *
 * Why this exists: `softSwitchOrganization` needs to tear down every
 * open stream before switching the active org, otherwise events from
 * Org A keep landing in state after the UI has moved to Org B. Hooks
 * that open streams register a `disconnect()` callback on mount and
 * deregister on unmount.
 *
 * Contract:
 *   - `register(key, disconnect)` returns the deregister function.
 *   - Call the returned function in the hook's cleanup effect.
 *   - `disconnectAll()` synchronously invokes every registered
 *     callback, swallowing individual errors so one bad cleanup can't
 *     block the rest. Called by `softSwitchOrganization`.
 *
 * Keys are arbitrary strings — scope them by hook name + resource id
 * (e.g. `"ignitePipeline:<runId>"`) so multiple concurrent streams of
 * the same kind don't clobber each other's entries.
 */

type DisconnectFn = () => void;

const disconnects = new Map<string, DisconnectFn>();

export function register(key: string, disconnect: DisconnectFn): () => void {
  disconnects.set(key, disconnect);
  return () => {
    // Only remove if the entry is still ours — guards against a later
    // re-register for the same key overwriting our slot before cleanup.
    if (disconnects.get(key) === disconnect) {
      disconnects.delete(key);
    }
  };
}

export function disconnectAll(): void {
  for (const [key, fn] of disconnects.entries()) {
    try {
      fn();
    } catch (err) {
      console.error(`[sseCleanupRegistry] disconnect failed for ${key}`, err);
    }
  }
  disconnects.clear();
}

export function size(): number {
  return disconnects.size;
}

/** Test-only: clear registry without calling disconnects. */
export function __resetForTests(): void {
  disconnects.clear();
}
